import React from 'react';
import { useLanguage } from '../contexts/language-context';
import type { NetworkDiagnostic, MimeTypeError } from '../utils/mime-type-diagnostic';

interface MimeDiagnosticProps {
  networkDiagnostic: NetworkDiagnostic | null;
  mimeErrors: MimeTypeError[];
}

export function MimeDiagnostic({ networkDiagnostic, mimeErrors }: MimeDiagnosticProps) {
  const { t } = useLanguage();

  return (
    <>
      {networkDiagnostic && (
        <div className="mb-6 p-4 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-white dark:bg-neutral-90">
          <h2 className="text-xl font-semibold mb-3 text-neutral-90 dark:text-white">🌐 {t('networkDiagnostic')}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-neutral-70 dark:text-neutral-30">
              <strong className="text-neutral-90 dark:text-white">{t('status')}:</strong>{' '}
              <span
                className={
                  networkDiagnostic.status === 200
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }
              >
                {networkDiagnostic.status} {networkDiagnostic.statusText}
              </span>
            </div>
            <div className="text-neutral-70 dark:text-neutral-30">
              <strong className="text-neutral-90 dark:text-white">{t('mimeType')}:</strong>{' '}
              <span
                className={
                  networkDiagnostic.mimeType?.includes('javascript') || networkDiagnostic.mimeType?.includes('module')
                    ? 'text-green-600 dark:text-green-400'
                    : networkDiagnostic.mimeType?.includes('text/html')
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }
              >
                {networkDiagnostic.mimeType || t('na')}
              </span>
            </div>
            <div className="text-neutral-70 dark:text-neutral-30">
              <strong className="text-neutral-90 dark:text-white">{t('redirected')}:</strong>{' '}
              <span
                className={
                  networkDiagnostic.redirected
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400'
                }
              >
                {networkDiagnostic.redirected ? `⚠️ ${t('yes')}` : `✅ ${t('no')}`}
              </span>
            </div>
            <div className="text-neutral-70 dark:text-neutral-30">
              <strong className="text-neutral-90 dark:text-white">{t('cors')}:</strong>{' '}
              <span
                className={
                  networkDiagnostic.cors ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
                }
              >
                {networkDiagnostic.cors ? `✅ ${t('ok')}` : `⚠️ ${t('check')}`}
              </span>
            </div>
            {networkDiagnostic.finalUrl && networkDiagnostic.finalUrl !== networkDiagnostic.url && (
              <div className="col-span-2 text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('finalUrl')}:</strong>{' '}
                {networkDiagnostic.finalUrl}
              </div>
            )}
            {networkDiagnostic.error && (
              <div className="col-span-2 text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('error')}:</strong>{' '}
                <span className="text-red-600 dark:text-red-400">{networkDiagnostic.error}</span>
              </div>
            )}
          </div>

          {networkDiagnostic.mimeType?.includes('text/html') && !networkDiagnostic.url.includes('.html') && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              <h3 className="font-semibold text-red-800 dark:text-red-300 mb-2">🚨 {t('mimeIssueDetected')}</h3>
              <p className="text-sm text-red-700 dark:text-red-400">{t('mimeIssueDescription')}</p>
              <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-400 mt-2 space-y-1">
                <li>{t('mimeIssueCause1')}</li>
                <li>{t('mimeIssueCause2')}</li>
                <li>{t('mimeIssueCause3')}</li>
                <li>{t('mimeIssueCause4')}</li>
                <li>{t('mimeIssueCause5')}</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {mimeErrors.length > 0 && (
        <div className="mb-6 p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
          <h2 className="text-xl font-semibold mb-3 text-red-800 dark:text-red-300">
            🚨 {t('mimeTypeErrors')} ({mimeErrors.length})
          </h2>
          <div className="space-y-3">
            {mimeErrors.map((error, index) => (
              <div
                key={index}
                className="p-3 bg-white dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      error.type === 'script'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                        : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                    }`}
                  >
                    {error.type.toUpperCase()}
                  </span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {error.timestamp.toLocaleTimeString()}
                  </span>
                </div>
                <div className="text-sm">
                  <p className="text-neutral-90 dark:text-white font-medium mb-1">{t('url')}:</p>
                  <p className="text-neutral-70 dark:text-neutral-300 font-mono text-xs break-all mb-2">{error.url}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-neutral-90 dark:text-white font-medium">{t('expected')}:</p>
                      <p className="text-green-700 dark:text-green-400 font-mono text-xs">{error.expected}</p>
                    </div>
                    <div>
                      <p className="text-neutral-90 dark:text-white font-medium">{t('received')}:</p>
                      <p className="text-red-700 dark:text-red-400 font-mono text-xs">{error.received}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">🧠 {t('intelligentAnalysis')}</h3>
            {(() => {
              const scriptErrors = mimeErrors.filter((e) => e.type === 'script').length;
              const cssErrors = mimeErrors.filter((e) => e.type === 'css').length;
              const htmlResponses = mimeErrors.filter((e) => e.received === 'text/html').length;
              const hasModuleScripts = mimeErrors.some((e) => e.type === 'script' && e.url.includes('.js'));
              const hasCSSFiles = mimeErrors.some((e) => e.type === 'css' && e.url.includes('.css'));

              if (htmlResponses === mimeErrors.length && mimeErrors.length > 1) {
                return (
                  <div className="text-sm text-blue-700 dark:text-blue-400">
                    <p className="font-medium mb-2">
                      🎯 {t('patternDetected')}: {t('fallbackRouting')}
                    </p>
                    <p>{t('fallbackRoutingDescription')}</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>{t('fallbackSolution1')}</li>
                      <li>{t('fallbackSolution2')}</li>
                      <li>{t('fallbackSolution3')}</li>
                    </ul>
                  </div>
                );
              }

              if (hasModuleScripts && scriptErrors > 0) {
                return (
                  <div className="text-sm text-blue-700 dark:text-blue-400">
                    <p className="font-medium mb-2">
                      🎯 {t('patternDetected')}: {t('moduleScriptIssue')}
                    </p>
                    <p>{t('moduleScriptDescription')}</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>{t('moduleScriptSolution1')}</li>
                      <li>{t('moduleScriptSolution2')}</li>
                      <li>{t('moduleScriptSolution3')}</li>
                    </ul>
                  </div>
                );
              }

              if (hasCSSFiles && cssErrors > 0) {
                return (
                  <div className="text-sm text-blue-700 dark:text-blue-400">
                    <p className="font-medium mb-2">
                      🎯 {t('patternDetected')}: {t('cssLoadingIssue')}
                    </p>
                    <p>{t('cssLoadingDescription')}</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>{t('cssLoadingSolution1')}</li>
                      <li>{t('cssLoadingSolution2')}</li>
                      <li>{t('cssLoadingSolution3')}</li>
                    </ul>
                  </div>
                );
              }

              return <p className="text-sm text-blue-700 dark:text-blue-400">{t('generalMimeAnalysis')}</p>;
            })()}
          </div>

          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">💡 {t('quickFixSolutions')}</h3>
            <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
              <li>{t('solution1')}</li>
              <li>{t('solution2')}</li>
              <li>{t('solution3')}</li>
              <li>{t('solution4')}</li>
              <li>{t('solution5')}</li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
